import React from 'react';
import {Box, Typography} from '@mui/material';
import {BuilderPot, BuilderPot_dentistChildren} from '../../../schema-types/BuilderPot';
import {PlanAccordionShell} from '../CreateBuilder/BuilderSourceDestination/PlanAccordionShell';
import {PlanSelector} from '../CreateBuilder/BuilderSourceDestination/PlanSelector';
import {PlanValidationIcon} from '../CreateBuilder/BuilderSourceDestination/PlanValidationIcon';
import {usePlanValidationLevel} from '../CreateBuilder/BuilderSourceDestination/usePlanValidationLevel';
import {
  getSummary,
  getSummaryParts,
} from '../CreateBuilder/BuilderSourceDestination/builderNaming';
import {BuilderContributeSourceFields} from './BuilderContributeSourceFields';
import {getCurrencyFloat} from '../../../utils/currencyFormatter';
import {ContributeSourceSubplan} from './ContributeSourceSubplan';
import {ParentPlanRow} from '../CreateBuilder/BuilderSourceDestination/ParentPlanRow';

interface ContributeSourceParentProps {
  pot: BuilderPot;
  contributeFromId?: string;
  greyed?: boolean;
  sameReview?: boolean;
  handleChange: (childId: string) => void;
}

export const ContributeSourceParent: React.FC<ContributeSourceParentProps> = ({
  pot,
  contributeFromId,
  greyed,
  sameReview,
  handleChange,
}) => {
  const dentistChildren: BuilderPot_dentistChildren[] = pot.dentistChildren ?? [];
  const hasChildren = dentistChildren.length > 0;
  const isLeafSelected = !hasChildren && contributeFromId === pot.id;
  const childSelected =
    hasChildren && dentistChildren.some((c) => c.id === contributeFromId);
  const activeFormikKey =
    isLeafSelected || childSelected ? 'contribute.contributeFrom' : undefined;
  const {bgClass} = usePlanValidationLevel(activeFormikKey);

  if (hasChildren) {
    const {label, currentValue} = getSummaryParts(pot);
    return (
      <ParentPlanRow
        pot={pot}
        isSelected={childSelected}
        sameReview={sameReview}
        validationLevel={bgClass}
        summaryChildren={
          <>
            <Typography variant="body1" sx={{fontWeight: 600}}>
              {label}
            </Typography>
            {currentValue && (
              <Typography
                variant="body2"
                sx={{fontWeight: 600, ml: 2, whiteSpace: 'nowrap'}}
              >
                {currentValue}
              </Typography>
            )}
          </>
        }
        summaryTrailing={
          childSelected && activeFormikKey ? (
            <PlanValidationIcon formikKeyPrefix={activeFormikKey} />
          ) : undefined
        }
      >
        {dentistChildren.map((child) => (
          <ContributeSourceSubplan
            key={child.id}
            subPlan={child}
            isSelected={contributeFromId === child.id}
            handleChange={() => handleChange(child.id)}
          />
        ))}
      </ParentPlanRow>
    );
  }

  return (
    <ParentPlanRow pot={pot} isSelected={isLeafSelected} sameReview={sameReview}>
      <PlanAccordionShell
        expanded={isLeafSelected}
        isSelected={isLeafSelected}
        isSoleParent
        validationLevel={bgClass}
        greyed={greyed}
        sameReview={sameReview}
        summaryChildren={
          <>
            <PlanSelector
              mode="single"
              id={pot.id}
              isSelected={isLeafSelected}
              handleChange={() => handleChange(pot.id)}
            />
            {getSummary(pot)}
          </>
        }
        summaryTrailing={
          activeFormikKey ? (
            <PlanValidationIcon formikKeyPrefix={activeFormikKey} />
          ) : undefined
        }
      >
        <BuilderContributeSourceFields
          pot={pot}
          maxAmount={getCurrencyFloat(pot.totalValue)}
        />
      </PlanAccordionShell>
    </ParentPlanRow>
  );
};
